import {KEY_POINT_TOKENS} from '../constants/tokens';

/**
 * СВЕЧЕНИЕ РУИН В РАУНДЫ С УВЕЛИЧЕННЫМ ДОХОДОМ. Пока руины приносят больше
 * очков (последние раунды партии, см. checkIfRoundWithIncreasedRuinsIncome),
 * поверх жетонов руин и великих руин на поле:
 *
 *  - ОРЕОЛ — мягкий белесый свет по краю жетона. Медленно «дышит»:
 *    разгорается и притухает, у каждого жетона в своем ритме, вразнобой;
 *  - ИКОНКА ОЧКОВ — белый силуэт колонны ровно поверх той, что нарисована на
 *    жетоне. Чуть светится в такт ореолу, а время от времени по ней снизу
 *    вверх проходит волна белого света — иконка переливается;
 *  - БЛЕСК — четырехлучевые звездочки то тут, то там вспыхивают на жетоне и
 *    гаснут. Только на великих руинах: металл блестит, а на камне обычных
 *    руин блеск смотрится чужим (решение владельца игры).
 *
 * Все лежит поверх жетона: из-под него свет закрывали бы соседние жетоны.
 * Свечение — украшение: в состояние партии не попадает и у каждого клиента
 * свое (см. scenes/RuinsIncomeGlow).
 */
export const RUINS_INCOME_GLOW = Object.freeze({
  // Слои — над жетоном (и над бликами на нем, +0.002), но под всем, что
  // лежит выше жетонов: флаги, спецстроения, горы, отряды
  depthOffsets: Object.freeze({
    rim: 0.003,
    icon: 0.003,
    shimmer: 0.0035,
    sparkle: 0.004,
  }),
  // Самый длинный шаг времени за кадр, мс. Время идет по кадрам: в скрытой
  // вкладке свечение стоит, а после возврата не прыгает
  maxStepMs: 100,
  // За сколько мс свечение разгорается, когда появляется: в начале первого
  // раунда с увеличенным доходом оно проступает, а не возникает разом
  fadeInMs: 1500,
  // Один вдох-выдох ореола и иконки, мс
  breathPeriodMs: 2800,

  rim: Object.freeze({
    // Яркость в самом слабом и самом сильном месте дыхания
    alphaMin: 0.3,
    alphaMax: 0.9,
    // Картинка — светящийся контур шестиугольника (вершины слева и справа,
    // как у жетона), рисуется в память один раз на сцену. hexWidth — ширина
    // контура в картинке; heightShare — высота жетона к ширине; insetShare —
    // насколько контур утоплен внутрь от края: свет идет полосой по краю
    // жетона изнутри, а наружу на соседние жетоны выходит немного;
    // lineShare и blurShare — толщина линии и размытие света в долях ширины
    texture: Object.freeze({
      key: 'ruins_income_glow_rim',
      hexWidth: 256,
      heightShare: 434 / 500,
      insetShare: 0.07,
      lineShare: 0.05,
      blurShare: 0.1,
    }),
  }),

  icon: Object.freeze({
    // Силуэт иконки вырезается из картинки обычных руин: на светлом камне
    // темная иконка отделяется чисто. У великих руин иконка та же и стоит там
    // же (сверено по картинкам), поэтому силуэт у них общий
    sourceTextureKey: KEY_POINT_TOKENS.RUINS,
    // Где иконка на картинке жетона (500×434), с запасом в пару пикселей
    // со всех сторон. Сверяет с картинками тест ruinsIncomeGlow.iconRect
    sourceRect: Object.freeze({x: 151, y: 104, width: 198, height: 225}),
    // Пиксель с яркостью до lumaDark — иконка целиком, от lumaLight — камень,
    // между ними — край иконки (полупрозрачный, без лесенки)
    lumaDark: 100,
    lumaLight: 150,
    // Силуэт хранится в таком масштабе к картинке жетона: на поле иконка
    // заметно меньше исходных 190 пикселей
    textureScale: 0.7,
    textureKey: 'ruins_income_glow_icon',
    // Яркость силуэта в такт ореолу: едва заметна — иконка остается
    // узнаваемой, переливается она волной
    alphaMin: 0.04,
    alphaMax: 0.16,
  }),

  shimmer: Object.freeze({
    // Волна света проходит по иконке за durationMs и повторяется в среднем
    // раз в periodMs (разброс — доля в обе стороны: у каждого жетона свой ритм)
    durationMs: 1400,
    periodMs: 3600,
    periodSpread: 0.15,
    // Ширина полосы света — доля диагонали иконки; волна идет из нижнего
    // левого угла в верхний правый
    bandShare: 0.35,
    // Мягкий ореол вокруг освещенной части иконки, пикселей картинки
    glowBlur: 4,
    alpha: 1,
    // Волна — заранее нарисованные кадры одной картинки (по силуэту иконки),
    // на поле лишь переключается кадр
    frameCount: 32,
    frameColumns: 8,
    textureKey: 'ruins_income_glow_shimmer',
  }),

  sparkles: Object.freeze({
    // Сколько звездочек одновременно может гореть на одном жетоне
    count: 2,
    // Вспышка от появления до угасания, мс: короткая — звездочка мелькает
    // бликом, а не разгорается
    durationMs: 500,
    // Пауза перед следующей вспышкой той же звездочки, мс. Вместе с числом
    // звездочек задает, КАК ЧАСТО жетон блестит: сейчас примерно раз в секунду
    delayMinMs: 600,
    delayMaxMs: 2400,
    // Где вспыхивают: в овале такой полуширины и полувысоты (доли ширины и
    // высоты жетона) — внутри жетона, не на краю
    areaWidthShare: 0.38,
    areaHeightShare: 0.36,
    // Размер звездочки в ширинах жетона и разброс — доля в обе стороны
    sizeShare: 0.2,
    sizeSpread: 0.3,
    // На сколько градусов звездочка поворачивается за вспышку
    spinDeg: 20,
    alpha: 1,
    texture: Object.freeze({
      key: 'ruins_income_glow_sparkle',
      size: 64,
      // Толщина луча у основания — доля размера картинки
      rayWidthShare: 0.06,
      // Радиус яркой сердцевины — доля размера картинки
      coreShare: 0.18,
    }),
  }),
});

/** Жетоны, которые светятся в раунды с увеличенным доходом руин */
const RUINS_INCOME_GLOW_TOKENS = Object.freeze([
  KEY_POINT_TOKENS.RUINS,
  KEY_POINT_TOKENS.SUPER_RUINS,
]);

/**
 * Светится ли жетон в раунды с увеличенным доходом руин
 * @param {string} tokenType
 * @returns {boolean}
 */
export const checkIfRuinsIncomeGlowToken = (tokenType) => RUINS_INCOME_GLOW_TOKENS.includes(tokenType);

/** Жетоны, на которых вспыхивают звездочки: металлические */
const RUINS_INCOME_GLOW_SPARKLE_TOKENS = Object.freeze([
  KEY_POINT_TOKENS.SUPER_RUINS,
]);

/**
 * Вспыхивают ли на светящемся жетоне звездочки
 * @param {string} tokenType
 * @returns {boolean}
 */
export const checkIfRuinsIncomeGlowSparkles = (tokenType) => RUINS_INCOME_GLOW_SPARKLE_TOKENS.includes(tokenType);
